import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexPermissionsUserMetadata } from 'features/permissions/hooks/useRolodexPermissionsUserMetadata'
import { GeckoPermsContextType } from 'features/permissions/types'
import { computePermissions } from 'features/permissions/utils/computePermissions'
import { doesHaveRequiredPermissionsBase } from 'features/permissions/utils/doesHaveRequiredPermissionsBase'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { RolodexPermissionsValues } from 'types'

export const GeckoPermissionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorization, authorizedAxios } = useGeckoAuth()

  const { data: currentUserMetadata, mutate: refreshCurrentUser } =
    useRolodexPermissionsUserMetadata(
      authorizedAxios,
      authorization,
      ROLODEX_URL
    )

  const [showPermissionGates, setShowPermissionGates] = useState<boolean>(false)

  useEffect(() => {
    if (!currentUserMetadata?.roles) return
  }, [currentUserMetadata])

  const { permissions } = useMemo(() => {
    // Need to wait for the mask to be set so we don't return inaccurate permissions
    if (!currentUserMetadata) {
      return { permissions: undefined }
    }
    return computePermissions(currentUserMetadata)
  }, [currentUserMetadata])

  const hasPermissions = useCallback(
    (toCheck: RolodexPermissionsValues[]) =>
      doesHaveRequiredPermissionsBase(permissions, toCheck),
    [permissions]
  )

  return (
    <GeckoPermsContext.Provider
      value={{
        user: currentUserMetadata,
        refreshCurrentUser: refreshCurrentUser,
        permissions,
        showPermissionGates,
        setShowPermissionGates,
        hasPermissions,
      }}>
      {children}
    </GeckoPermsContext.Provider>
  )
}

export const GeckoPermsContext = createContext<
  GeckoPermsContextType | undefined
>(undefined)
